import {
  createAccount,
  fetchAccountDetails,
  fetchAccounts,
  fetchFinancialAccounts,
  fetchProformaLineItems,
  saveFinancialAccountMappings,
  fetchAllProperties,
  upsertPortfolio,
  upsertProperty,
  upsertUser,
} from "@/services/voloService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const useAccountDetails = (accountId: string) => {
  const queryClient = useQueryClient();

  return {
    query: useQuery({
      queryKey: ["accountDetails"],
      queryFn: () => fetchAccountDetails(accountId),
      enabled: !!accountId,
    }),
    invalidateCb: () => {
      queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
    },
  };
};

const useAccountList = () => {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: fetchAccounts,
  });
};

const useAllProperties = () => {
  return useQuery({
    queryKey: ["allProperties"],
    queryFn: fetchAllProperties,
  });
};

const useCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAccount,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
};

const useUpsertPortfolio = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertPortfolio,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
    },
  });
};

const useUpsertProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertProperty,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
    },
  });
};

const useUpsertUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertUser,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
    },
  });
};

const useFinancialAccounts = (accountId: string) => {
  return useQuery({
    queryKey: ["financialAccounts", accountId],
    queryFn: () => fetchFinancialAccounts(accountId),
    enabled: !!accountId,
  });
};

const useProformaLineItems = (accountId: string) => {
  return useQuery({
    queryKey: ["proformaLineItems", accountId],
    queryFn: () => fetchProformaLineItems(accountId),
    enabled: !!accountId,
  });
};

const useSaveFinancialAccountMappings = (accountId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mappings }: { mappings: any }) =>
      accountId ? saveFinancialAccountMappings(accountId, mappings) : Promise.reject('No accountId'),
    onSettled: () => {
      // Invalidate both account details and financial accounts queries
      queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
      if (accountId) queryClient.invalidateQueries({ queryKey: ["financialAccounts", accountId] });
    },
  });
};

export {
  useAccountDetails,
  useAccountList,
  useAllProperties,
  useCreateAccount,
  useUpsertPortfolio,
  useUpsertProperty,
  useUpsertUser,
  useFinancialAccounts,
  useProformaLineItems,
  useSaveFinancialAccountMappings,
};
